import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, ArrowUpRight, Check, Zap, ShieldCheck, Sparkles,
  PlugZap, Cog,
} from "lucide-react";

/**
 * /integrations — where lead data lands after Connexis routes it.
 *
 * Buyers want to know: "will this plug into what I already use?" This page
 * says yes with a clean logo grid, honest status per platform (Native vs
 * Webhook), and a fast path to Docs / Contact for anything bespoke.
 *
 * Each card is a soft "we've built this" statement, not a marketing claim —
 * "Native" = we push into the platform's own API objects; "Webhook" = we POST
 * signed JSON to your endpoint and your team receives it.
 */

const INTEGRATIONS = [
  {
    slug: "hubspot",
    name: "HubSpot",
    status: "Native",
    body: "Push accepted leads straight into your HubSpot pipeline. Deals + contacts created with sector, source and CPL. Two-way sync so disposition flows back to the routing engine.",
    perks: ["Contact + Deal creation", "Custom-property mapping", "Owner assignment + task triggers", "Two-way disposition sync"],
    Logo: HubSpotLogo,
    docs: "/docs#webhooks",
  },
  {
    slug: "salesforce",
    name: "Salesforce",
    status: "Native",
    body: "Direct Lead / Opportunity insertion into your Salesforce org. Bulk API on peak, streaming on sub-second delivery. Field mappings survive Salesforce upgrades.",
    perks: ["Lead + Opportunity insert", "Custom field mappings", "Bulk API on peaks", "Streaming on sub-second delivery"],
    Logo: SalesforceLogo,
    docs: "/docs#webhooks",
  },
  {
    slug: "pipedrive",
    name: "Pipedrive",
    status: "Native",
    body: "Automatic Person + Deal creation with sector tags, custom-field mapping and activity scheduling — so your first follow-up is booked before your team even sees the notification.",
    perks: ["Person + Deal creation", "Custom-field mapping", "Activity auto-scheduling", "Pipeline-stage routing"],
    Logo: PipedriveLogo,
    docs: "/docs#webhooks",
  },
  {
    slug: "zapier",
    name: "Zapier",
    status: "Webhook",
    body: "Trigger any of Zapier's 6,000+ integrations from a Connexis lead-purchased event. Ideal for lightweight ops (Slack alerts, Google Sheets logging, Notion updates) without a code deploy.",
    perks: ["Lead events as Zap triggers", "6,000+ downstream apps", "No-code recipe library", "Free tier compatible"],
    Logo: ZapierLogo,
    docs: "/docs#webhooks",
  },
  {
    slug: "webhooks",
    name: "Custom Webhooks",
    status: "Native",
    body: "The universal fallback — HMAC-signed POST to any HTTPS endpoint your engineering team owns. Stripe-style retry schedule (30s → 4h, 5 attempts) and full replay from the dashboard.",
    perks: ["HMAC-SHA256 signed", "Stripe-style retries", "Full replay UI", "Per-event schema in Docs"],
    Logo: WebhookLogo,
    docs: "/docs#webhooks",
  },
  {
    slug: "sheets",
    name: "Google Sheets",
    status: "Via Zapier",
    body: "Log every accepted lead into a shared sheet for reporting or manual triage. Handy for pilot buyers before they wire us into their production CRM.",
    perks: ["Row-per-lead", "Custom column mapping", "Pilot-friendly", "Zero engineering effort"],
    Logo: SheetsLogo,
    docs: "/docs#webhooks",
  },
];

/* ---------------------------------------------------------- */

export default function Integrations() {
  return (
    <MarketingLayout>
      <div className="bg-white" data-testid="integrations-page">

        {/* ─── HERO ─── */}
        <section className="relative border-b border-slate-200 overflow-hidden bg-gradient-to-b from-white to-slate-50/30">
          <div className="absolute inset-0 dot-grid dot-grid-fade opacity-70" />
          <div className="absolute -top-32 -right-20 w-[700px] h-[700px] rounded-full bg-[#3d7bff]/[0.07] blur-3xl pointer-events-none" />
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32">
            <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
              {/* LEFT — copy + CTAs */}
              <div>
                <div className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] uppercase font-semibold text-[#0055FF]">
                  <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Integrations · plumb-in
                </div>
                <h1 className="mt-3 text-5xl sm:text-6xl lg:text-[64px] tracking-[-0.045em] font-semibold text-slate-900 leading-[1.02]" data-testid="integrations-title">
                  Land in the tools{" "}
                  <span className="bg-gradient-to-r from-slate-900 via-[#0055FF] to-[#3d7bff] bg-clip-text text-transparent">
                    your team already runs.
                  </span>
                </h1>
                <p className="mt-5 max-w-xl text-[17px] text-slate-600 leading-relaxed">
                  Every accepted lead arrives natively where you work — HubSpot, Salesforce, Pipedrive, Zapier or an
                  HMAC-signed webhook to whatever your engineers have built. No middleware to buy, no CSV shuffling.
                </p>

                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <Link to="/buy-leads">
                    <Button className="rounded-full px-5 h-11 bg-[#0055FF] hover:bg-[#0044CC] shadow-[0_8px_24px_rgba(0,85,255,0.35)]" data-testid="integrations-hero-cta">
                      Start receiving leads <ArrowUpRight className="size-4" />
                    </Button>
                  </Link>
                  <Link to="/docs">
                    <Button variant="outline" className="rounded-full px-5 h-11 border-slate-300 hover:border-[#0055FF]/40" data-testid="integrations-hero-docs">
                      Read the docs
                    </Button>
                  </Link>
                  <Link to="/contact" className="text-[13px] font-medium text-[#0055FF] hover:underline inline-flex items-center gap-1 ml-1">
                    Need something bespoke? <ArrowRight className="size-3.5" />
                  </Link>
                </div>

                {/* Trust strip — same rhythm as /solutions */}
                <div className="mt-10 pt-8 border-t border-slate-200 grid grid-cols-3 gap-6 max-w-md">
                  <Stat v="< 1s" l="delivery latency" />
                  <Stat v="HMAC" l="signed payloads" />
                  <Stat v="5×" l="retry-on-failure" />
                </div>
              </div>

              {/* RIGHT — orbital ring */}
              <div className="relative">
                <LogoCluster />
                {/* Legend */}
                <div className="mt-3 grid grid-cols-3 gap-2 text-[11px] text-center text-slate-500">
                  <LegendChip color="bg-emerald-500" label="Native" />
                  <LegendChip color="bg-[#0055FF]" label="Webhook" />
                  <LegendChip color="bg-slate-400" label="Via Zapier" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── LOGO GRID ─── */}
        <section className="py-20 sm:py-24" id="grid">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0055FF]">Native destinations</p>
              <h2 className="mt-3 text-3xl sm:text-4xl tracking-[-0.03em] font-medium text-slate-900">Six ways in. One outcome — leads in your workflow.</h2>
              <p className="mt-4 text-slate-600 text-[15px] leading-relaxed">
                Each integration is production-grade: signed payloads, idempotent inserts, retry-on-failure and full replay from your dashboard.
                Something bespoke? We build custom connectors as part of our <Link to="/services#stack" className="text-[#0055FF] hover:underline">CRM Setup service</Link>.
              </p>
            </div>

            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="integrations-grid">
              {INTEGRATIONS.map((it) => (
                <IntegrationCard key={it.slug} {...it} />
              ))}
            </div>
          </div>
        </section>

        {/* ─── HOW IT WORKS ─── */}
        <section className="py-20 border-t border-slate-200 bg-slate-50/60">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0055FF]">How it works</p>
              <h2 className="mt-3 text-3xl sm:text-4xl tracking-[-0.03em] font-medium text-slate-900">Three steps to live.</h2>
            </div>
            <div className="mt-12 grid md:grid-cols-3 gap-5">
              {[
                { n: "01", Icon: PlugZap, t: "Pick your destination", d: "Choose from a native connector, Zapier or a custom webhook — whichever fits your stack." },
                { n: "02", Icon: Cog,     t: "Map your fields",       d: "We pre-fill sector-standard mappings. Your team overrides anything bespoke in the dashboard." },
                { n: "03", Icon: Zap,     t: "Receive signed leads",  d: "Every accepted lead flows straight into your CRM within seconds — signed, idempotent, and traceable." },
              ].map((x) => (
                <div key={x.n} className="rounded-2xl border border-slate-200 bg-white p-7">
                  <span className="font-mono text-sm text-[#0055FF]">{x.n}</span>
                  <span className="inline-grid place-items-center size-9 rounded-lg bg-slate-900 text-white ml-3">
                    <x.Icon className="size-4" strokeWidth={1.7} />
                  </span>
                  <h3 className="mt-5 text-lg font-medium text-slate-900">{x.t}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{x.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── CTA ─── */}
        <section className="py-20 bg-slate-900 text-white">
          <div className="mx-auto max-w-3xl px-6 text-center">
            <Sparkles className="size-6 mx-auto text-[#60A5FA]" />
            <h2 className="mt-4 text-3xl sm:text-4xl tracking-[-0.03em] font-medium leading-tight">
              Don&apos;t see your stack?
            </h2>
            <p className="mt-4 text-white/70 text-[15px] leading-relaxed max-w-xl mx-auto">
              We&apos;ve built connectors for Zoho, GHL, Salesloft, ActiveCampaign, Freshsales and half a dozen bespoke enterprise CRMs.
              If you can name it, we can plumb into it.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link to="/contact">
                <Button className="rounded-full px-5 h-11 bg-white text-slate-900 hover:bg-slate-100" data-testid="integrations-cta-contact">
                  Talk to engineering <ArrowUpRight className="size-4" />
                </Button>
              </Link>
              <Link to="/docs">
                <Button variant="outline" className="rounded-full px-5 h-11 border-white/25 bg-transparent hover:bg-white/10 text-white" data-testid="integrations-cta-docs">
                  Read the docs <ArrowRight className="size-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
}

/* ──────────── Hero helpers (match /solutions rhythm) ──────────── */
function Stat({ v, l }) {
  return (
    <div>
      <div className="text-[26px] sm:text-[28px] tracking-[-0.02em] font-semibold text-slate-900 tabular-nums leading-none">{v}</div>
      <div className="mt-2 text-[10.5px] uppercase tracking-[0.16em] text-slate-500 font-semibold">{l}</div>
    </div>
  );
}
function LegendChip({ color, label }) {
  return (
    <div className="inline-flex items-center gap-1.5 justify-center py-1 rounded-full">
      <span className={`size-1.5 rounded-full ${color}`} />
      <span>{label}</span>
    </div>
  );
}

/* ──────────── Card ──────────── */
function IntegrationCard({ slug, name, status, body, perks, Logo, docs }) {
  const statusTone =
    status === "Native"     ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
    status === "Webhook"    ? "bg-[#0055FF]/8 text-[#0055FF] border-[#0055FF]/20" :
                              "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <div
      className="group rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#0055FF]/40 hover:shadow-[0_16px_50px_-24px_rgba(0,85,255,0.25)] transition-all"
      data-testid={`integration-card-${slug}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="inline-grid place-items-center size-12 rounded-xl bg-slate-50 border border-slate-200 group-hover:bg-white transition-colors">
          <Logo />
        </div>
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-mono uppercase tracking-[0.14em] ${statusTone}`}>
          {status === "Native" && <Check className="size-3" />}
          {status}
        </span>
      </div>
      <h3 className="mt-5 text-[18px] font-semibold text-slate-900 tracking-tight">{name}</h3>
      <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">{body}</p>
      <ul className="mt-4 space-y-1.5">
        {perks.map((p) => (
          <li key={p} className="flex items-start gap-2 text-[12.5px] text-slate-700">
            <Check className="size-3.5 mt-0.5 text-[#0055FF] shrink-0" strokeWidth={2.4} /> {p}
          </li>
        ))}
      </ul>
      <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
        <Link to={docs} className="text-[12.5px] font-medium text-[#0055FF] hover:underline inline-flex items-center gap-1" data-testid={`integration-docs-${slug}`}>
          View docs <ArrowUpRight className="size-3.5" />
        </Link>
        <Link to="/contact" className="text-[12px] text-slate-500 hover:text-slate-900">
          Bespoke setup →
        </Link>
      </div>
    </div>
  );
}

/* ──────────── Hero orbital — Connexis at centre, integration logos orbiting ──────────── */
function LogoCluster() {
  // 6 logos, evenly distributed around a single orbit. Follows the same
  // rotate-orbit + counter-rotate-icon pattern as /solutions OrbitalEcosystem
  // so icons stay upright as the ring spins.
  const NODES = [
    { Logo: HubSpotLogo,    label: "HubSpot" },
    { Logo: SalesforceLogo, label: "Salesforce" },
    { Logo: PipedriveLogo,  label: "Pipedrive" },
    { Logo: ZapierLogo,     label: "Zapier" },
    { Logo: WebhookLogo,    label: "Webhooks" },
    { Logo: SheetsLogo,     label: "Sheets" },
  ];
  return (
    <div className="relative w-full aspect-square max-w-[480px] mx-auto select-none" data-testid="integrations-cluster">
      <style>{`
        @keyframes intOrbit  { from { transform: translate(-50%, -50%) rotate(0deg); } to { transform: translate(-50%, -50%) rotate(360deg); } }
        @keyframes intCtr    { from { transform: rotate(0deg); } to { transform: rotate(-360deg); } }
        @keyframes intHub    {
          0%, 100% { box-shadow: 0 0 30px rgba(0,85,255,0.45), 0 0 80px rgba(0,85,255,0.22); }
          50%      { box-shadow: 0 0 50px rgba(0,85,255,0.75), 0 0 130px rgba(0,85,255,0.36); }
        }
        .int-orbit { animation: intOrbit 60s linear infinite; }
        .int-ctr   { animation: intCtr   60s linear infinite; }
        .int-hub   { animation: intHub 2.4s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .int-orbit, .int-ctr, .int-hub { animation: none; }
        }
      `}</style>

      {/* Background wash — feels like the /solutions hero */}
      <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_center,rgba(0,85,255,0.08),transparent_65%)] pointer-events-none" />

      {/* Dashed orbit ring (static — rotating a circle is visually identical) */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-slate-300/60 pointer-events-none"
        style={{ width: "88%", height: "88%" }}
        aria-hidden
      />

      {/* Rotating orbit container with 6 nodes */}
      <div
        className="absolute top-1/2 left-1/2 int-orbit"
        style={{ width: "88%", height: "88%", transform: "translate(-50%, -50%)" }}
      >
        {NODES.map((it, i) => {
          const slot = (i * 360) / NODES.length;
          return (
            <div
              key={i}
              className="absolute inset-0 pointer-events-none"
              style={{ transform: `rotate(${slot}deg)` }}
            >
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                <div className="pointer-events-none" style={{ transform: `rotate(${-slot}deg)` }}>
                  <div className="int-ctr pointer-events-none">
                    <OrbitLogo Logo={it.Logo} label={it.label} />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* CENTER HUB — Connexis mark */}
      <div className="absolute inset-0 grid place-items-center pointer-events-none z-10">
        <span className="int-hub inline-grid place-items-center size-20 sm:size-24 rounded-2xl bg-[#0a1124] ring-1 ring-white/15">
          <svg viewBox="0 0 24 24" className="size-9 sm:size-10" fill="none" stroke="currentColor" strokeWidth="2.1">
            <path d="M5 7h7M5 12h12M5 17h7" strokeLinecap="round" stroke="#ffffff" />
            <circle cx="18" cy="7"  r="2.4" fill="#3d7bff" stroke="none" />
            <circle cx="18" cy="17" r="2.4" fill="#3d7bff" stroke="none" />
          </svg>
        </span>
      </div>
    </div>
  );
}

function OrbitLogo({ Logo, label }) {
  return (
    <div className="relative inline-block group/orbit pointer-events-auto">
      <span className="inline-grid place-items-center size-12 rounded-xl bg-white border border-slate-200 shadow-soft cursor-pointer transition-transform group-hover/orbit:scale-110">
        <Logo />
      </span>
      <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-2 -translate-y-full whitespace-nowrap rounded-md bg-slate-900 text-white text-[10.5px] font-medium tracking-wide px-2 py-1 opacity-0 group-hover/orbit:opacity-100 transition-opacity z-[60] shadow-lg">
        {label}
      </span>
    </div>
  );
}

/* ──────────── Inline brand marks (no external assets) ──────────── */
function HubSpotLogo() {
  return (
    <svg viewBox="0 0 32 32" className="size-6" aria-label="HubSpot">
      <circle cx="24" cy="22" r="4.5" fill="none" stroke="#FF7A59" strokeWidth="2.2" />
      <path d="M22 18 L14 10" stroke="#FF7A59" strokeWidth="2.2" strokeLinecap="round" />
      <circle cx="12" cy="8" r="3.5" fill="#FF7A59" />
      <rect x="10" y="18" width="4" height="8" rx="1" fill="#FF7A59" />
    </svg>
  );
}
function SalesforceLogo() {
  return (
    <svg viewBox="0 0 32 20" className="size-7" aria-label="Salesforce">
      <path
        d="M12 4a5 5 0 0 1 9 1.4 4.2 4.2 0 0 1 3.6 6.2A4 4 0 0 1 22 18h-9a4.5 4.5 0 0 1-4-6.6A4.6 4.6 0 0 1 12 4z"
        fill="#00A1E0"
      />
    </svg>
  );
}
function PipedriveLogo() {
  return (
    <svg viewBox="0 0 32 32" className="size-6" aria-label="Pipedrive">
      <rect x="6" y="6" width="20" height="20" rx="4" fill="#26292C" />
      <path d="M14 20v-8h4a2.6 2.6 0 0 1 0 5.2h-1.8V20h-2.2z" fill="#F7A200" />
    </svg>
  );
}
function ZapierLogo() {
  return (
    <svg viewBox="0 0 32 32" className="size-6" aria-label="Zapier">
      <g fill="#FF4A00">
        <rect x="14.4" y="4" width="3.2" height="10" rx="0.6" />
        <rect x="14.4" y="18" width="3.2" height="10" rx="0.6" />
        <rect x="4" y="14.4" width="10" height="3.2" rx="0.6" />
        <rect x="18" y="14.4" width="10" height="3.2" rx="0.6" />
        <rect x="6.4" y="7" width="3.2" height="10" rx="0.6" transform="rotate(-45 8 12)" />
        <rect x="22.4" y="7" width="3.2" height="10" rx="0.6" transform="rotate(45 24 12)" />
      </g>
    </svg>
  );
}
function WebhookLogo() {
  return (
    <svg viewBox="0 0 32 32" className="size-6" aria-label="Webhooks">
      <g fill="none" stroke="#0055FF" strokeWidth="2.2" strokeLinecap="round">
        <circle cx="10" cy="10" r="3" fill="#0055FF" />
        <circle cx="22" cy="10" r="3" fill="#0055FF" />
        <circle cx="16" cy="24" r="3" fill="#0055FF" />
        <path d="M12 12 L15 22" />
        <path d="M20 12 L17 22" />
        <path d="M11 10 L21 10" />
      </g>
    </svg>
  );
}
function SheetsLogo() {
  return (
    <svg viewBox="0 0 32 32" className="size-6" aria-label="Google Sheets">
      <rect x="7" y="4" width="18" height="24" rx="2" fill="#0F9D58" />
      <rect x="10" y="10" width="12" height="1.8" fill="#fff" />
      <rect x="10" y="14" width="12" height="1.8" fill="#fff" />
      <rect x="10" y="18" width="12" height="1.8" fill="#fff" />
      <rect x="10" y="22" width="12" height="1.8" fill="#fff" />
      <rect x="15" y="10" width="0.6" height="14" fill="#0F9D58" />
    </svg>
  );
}
